import sortBy from 'lodash/sortBy';
import {
  CABIN_BAGGAGE,
  cabinBaggage,
  CHECK_IN_BAGGAGE,
  secondBaggagePerTraveler,
  MUTUALLY_EXCLUSIVE,
  QPASS,
} from '@eti/products';
import type {
  AncillaryBundlesOnTdPageFragment,
  AgencyAccount,
  GetSelectedTripOnTravelerDetailsPageQuery,
  AvailableExtraProductsOnTdPageFragment,
  GetTravelerInformationDataQuery,
  GetBookingStepPageDataQuery,
} from '@eti/schema-types';
import { TripType } from '@eti/schema-types';
import { INCLUDED_PRODUCTS, SUPPORTED_PRODUCTS } from '../../constants/productConstants';
import { INCLUDED_CABIN_BAGGAGE } from '../../constants/includedBaggageConstants';
import { getTripNotificationChanges } from '../../utils/notification';
import { transformPaymentMethod } from '../../utils/paymentMethodsTS';
import { transformBundleData } from '../../utils/transformBundleData';
import { getInitialValuesForAgencyForm } from '../../pages/traveler-details/utils/agentInformation';
import { hasExcludedSystemProvider } from '../../pages/traveler-details/utils/productPerTraveler';
import {
  addExtraProductsSelectedOnTrip,
  filterProductCards,
  generateInitialValuesExtraProducts,
  getClimateCompensationPriceWhenSelectedWithTrip,
  getProductIndex,
} from '../../pages/traveler-details/utils/products';
import { generateInitialValuesAncillaryBundles } from '../../pages/traveler-details/utils/bundles';
import { getInitialValuesForContactInformationForm } from '../../pages/traveler-details/utils/contactInformation';
import { getInitialValuesTravelerForm } from '../../pages/traveler-details/utils/initialValues';
import {
  transformMutuallyExclusiveData,
  getProductsFrom,
  generateProductComponent,
  populateBoundsWithBaggageInSelectedBundle,
  isBaggageSelectedWithBundle,
  transformCountry,
  getAncillaryBundleProduct,
  getDBFProductsFrom,
} from '../../pages/traveler-details/utils/formatUtils';
import type {
  NonNullableTravelerDetailsQueryIncludedExtraProductType,
  TravelerDetailsQueryBoundSegmentType,
  TravelerDetailsQueryBoundType,
  TravelerDetailsQueryIncludedExtraProductType,
  TravelerDetailsQuerySelectedTripType,
} from '../types/queries/getSelectedTripOnTravelerDetailsPageQuery/models';
import { transformTravelerInformationQueryToFormShape } from '../../pages/booking-step/containers/utils/travelerInformationUtils';
import { sortAccordingToArray, transformTravelersData } from './utils';
import type {
  CountryType,
  ExtraProduct,
  GenerateProductComponentReturnFunctionReturnType,
  GetProductsFromReturnType,
  PrimaryOrSecondaryProductType,
  StructuredExtraComponentType,
  SupportedProductsType,
  TransformDataPropertiesPropsType,
  TransformedTravelersDataType,
  TransformExtraProductsDataType,
} from './models';

const getTripOperatingCarriers = (bounds: TravelerDetailsQueryBoundType[]) => {
  const uniqueAndTruthyValues = (carrierCode: string, index: number, carrierCodeArray: string[]) =>
    carrierCode && carrierCodeArray.indexOf(carrierCode) === index;

  return (
    (typeof bounds === 'object' &&
      bounds?.reduce((accumulatedCarriersCodes: string[] = [], bound) => {
        if (!bound) {
          accumulatedCarriersCodes.push('');
        } else {
          accumulatedCarriersCodes.push(
            ...bound.segments?.map((segment: TravelerDetailsQueryBoundSegmentType) =>
              segment && 'operatingCarrier' in segment && segment.operatingCarrier.code
                ? segment.operatingCarrier.code
                : '',
            ),
          );
        }
        return accumulatedCarriersCodes;
      }, [])) ||
    []
  ).filter(uniqueAndTruthyValues);
};

const getExtraProductStructure = (
  product: AvailableExtraProductsOnTdPageFragment,
  shouldOverridePersuasion: boolean,
) => ({
  ...product,
  persuasion: {
    isEnabled:
      !shouldOverridePersuasion &&
      Boolean(product.configuration?.travelerDetails.persuasionEnabled),
  },
  shouldShowPricePerBound: Boolean(product.configuration?.travelerDetails.showPricePerBound),
  productPreSelection: product.configuration?.productPreSelection,
});

export const turnStringIntoArrayWithLowerCase = (string: string) => {
  return string.split(',').map((x) => x.trim().toLowerCase());
};

export const showProductFirst = (condition: boolean, productName: string, sortOrder: string) =>
  condition ? `${productName}, ${sortOrder}` : sortOrder;

export const addAncillaryBundleToProducts = (
  availableAncillaryBundlesFromQueryExist: boolean,
  isAncillaryBundlingEnabled: boolean,
  productCodes: string,
) =>
  isAncillaryBundlingEnabled && availableAncillaryBundlesFromQueryExist
    ? `ANCILLARY_BUNDLE, ${productCodes}`
    : productCodes;

const sortProductsAccordingToSortOrder = (
  availableAncillaryBundlesFromQueryExist: boolean,
  isAncillaryBundlingEnabled: boolean,
  products: ExtraProduct[],
  productsSortOrder: string,
): ExtraProduct[] => {
  if (!productsSortOrder) {
    return sortBy(products, ['index']);
  }

  const productsSortOrderWithBundle = showProductFirst(
    isAncillaryBundlingEnabled && availableAncillaryBundlesFromQueryExist,
    'ANCILLARY_BUNDLE',
    productsSortOrder,
  );

  const productsSortOrderWithQpass = showProductFirst(
    products.findIndex((product) => (product as { id: number }).id === QPASS.id) !== -1,
    'QPASS',
    productsSortOrderWithBundle,
  );

  return products.sort(
    sortAccordingToArray(turnStringIntoArrayWithLowerCase(productsSortOrderWithQpass)),
  );
};

const mutuallyExclusiveOnTop = (
  a: PrimaryOrSecondaryProductType,
  b: PrimaryOrSecondaryProductType,
) => {
  if ('id' in a && a.id === MUTUALLY_EXCLUSIVE.id) {
    return -1;
  }
  if ('id' in b && b.id === MUTUALLY_EXCLUSIVE.id) {
    return 1;
  }
  return 0;
};

export const splitProductsIntoPrimaryAndSecondary = (
  availableAncillaryBundlesFromQueryExist: boolean,
  isAncillaryBundlingEnabled: boolean,
  primaryProductCodes: string,
  products: ExtraProduct[],
): ExtraProduct[][] => {
  const primaryProductCodesArray = turnStringIntoArrayWithLowerCase(
    addAncillaryBundleToProducts(
      availableAncillaryBundlesFromQueryExist,
      isAncillaryBundlingEnabled,
      primaryProductCodes,
    ),
  );
  const primaryProducts = products.filter((product) =>
    primaryProductCodesArray.includes('code' in product ? product.code.toLowerCase() : ''),
  );
  const secondaryProducts = products
    .filter(
      (product) =>
        !primaryProductCodesArray.includes('code' in product ? product.code.toLowerCase() : ''),
    )
    .sort(mutuallyExclusiveOnTop);

  return [primaryProducts, secondaryProducts];
};

export const sortProducts = (
  availableAncillaryBundlesFromQueryExist: boolean,
  isAncillaryBundlingEnabled: boolean,
  isProductsPrimarySecondaryEnabled: boolean,
  primaryProductCodes: string,
  productsSortOrder: string,
  products: ExtraProduct[],
): ExtraProduct[][] => {
  const sortedProducts = sortProductsAccordingToSortOrder(
    availableAncillaryBundlesFromQueryExist,
    isAncillaryBundlingEnabled,
    products,
    productsSortOrder,
  );

  if (isProductsPrimarySecondaryEnabled) {
    return splitProductsIntoPrimaryAndSecondary(
      availableAncillaryBundlesFromQueryExist,
      isAncillaryBundlingEnabled,
      primaryProductCodes,
      sortedProducts,
    );
  }
  return [sortedProducts];
};

const getIncludedCabinBaggageComponent = (
  notAvailableProducts: GenerateProductComponentReturnFunctionReturnType[],
  cabinBaggageTripSummary: NonNullable<TravelerDetailsQuerySelectedTripType>['cabinBaggageTripSummary'],
  productsToDisplayPerTraveler: string,
) => {
  if (
    productsToDisplayPerTraveler &&
    productsToDisplayPerTraveler.includes('CABIN_BAGGAGE_PER_TRAVELER')
  ) {
    const baggageStatus =
      cabinBaggageTripSummary?.cabinBaggageTravelerDetails?.[0]?.cabinBaggageStatus;

    if (baggageStatus && baggageStatus === INCLUDED_CABIN_BAGGAGE) {
      const product = notAvailableProducts.filter((prod) => prod.id === cabinBaggage.id);
      const filteredNotAvailableProducts = notAvailableProducts.filter(
        (prod) => prod.id !== cabinBaggage.id,
      );

      return {
        productToBeAddedAsIncluded: product,
        filteredNotAvailableProducts,
      };
    }
  }

  return {
    productToBeAddedAsIncluded: [],
    filteredNotAvailableProducts: notAvailableProducts,
  };
};

const filterMutuallyExclusiveProducts = (
  supportedProduct: any,
  mutuallyExclusiveProductsToFilter: number[],
) =>
  !mutuallyExclusiveProductsToFilter.find(
    (mutuallyExclusiveProductId) => supportedProduct.id === mutuallyExclusiveProductId,
  );

const isBaggage = (product: any) => {
  return product.id === CHECK_IN_BAGGAGE.id || product.id === CABIN_BAGGAGE.id;
};

const transformExtraProductsData = ({
  ancillaryBundleId,
  ancillaryBundleOptions,
  availableAncillaryBundlesFromQueryExist,
  bounds,
  discountPercentage,
  isAncillaryBundlingEnabled,
  isDBF,
  isMutuallyExclusiveEnabled,
  isProductsPrimarySecondaryEnabled,
  mutuallyExclusiveProducts,
  primaryProductCodes,
  productsSortOrder,
  productsToDisplayPerTraveler,
  selectedTrip,
  shouldOverridePersuasion,
  shouldShowProductsPerTraveler,
  travelers,
}: TransformExtraProductsDataType) => {
  const cabinBaggageTripSummary = selectedTrip?.cabinBaggageTripSummary;
  const checkedBaggageSummary = selectedTrip?.checkedBaggageSummary;
  const includedExtraProducts: TravelerDetailsQueryIncludedExtraProductType[] =
    selectedTrip?.includedExtraProducts || [];
  const nonNullIncludedExtraProducts = includedExtraProducts.filter(
    (includedExtraProduct) => includedExtraProduct,
  ) as NonNullableTravelerDetailsQueryIncludedExtraProductType[];
  const personalItemTripSummary = selectedTrip?.personalItemTripSummary;
  const availableExtraProducts = ((selectedTrip &&
    'availableExtraProducts' in selectedTrip &&
    selectedTrip.availableExtraProducts) ||
    (selectedTrip &&
      'dynamicBookingFlow' in selectedTrip &&
      selectedTrip.dynamicBookingFlow &&
      selectedTrip.dynamicBookingFlow.stepProducts
        .availableExtraProducts)) as AvailableExtraProductsOnTdPageFragment[];

  const structuredExtraProducts: StructuredExtraComponentType[] = availableExtraProducts.map(
    (product) => getExtraProductStructure(product, shouldOverridePersuasion),
  );
  const supportedStructuredExtraProducts = structuredExtraProducts.filter(({ id: productId }) =>
    SUPPORTED_PRODUCTS.some(
      ({ id: supportedProductId }) => supportedProductId === Number(productId),
    ),
  );

  const ancillaryBundleComponent = getAncillaryBundleProduct(
    ancillaryBundleId,
    ancillaryBundleOptions,
    discountPercentage,
    isAncillaryBundlingEnabled,
    travelers,
  );

  const {
    components: mutuallyExclusiveComponents,
    productsToFilter: mutuallyExclusiveProductsToFilter,
  } = transformMutuallyExclusiveData(
    supportedStructuredExtraProducts || [],
    mutuallyExclusiveProducts || [],
    isMutuallyExclusiveEnabled,
  );

  const hasTwoMutuallyExclusiveProducts = mutuallyExclusiveProductsToFilter.length === 2;

  const filteredSupportedProducts: SupportedProductsType = hasTwoMutuallyExclusiveProducts
    ? SUPPORTED_PRODUCTS.filter((supportedProduct) =>
        filterMutuallyExclusiveProducts(supportedProduct, mutuallyExclusiveProductsToFilter),
      )
    : SUPPORTED_PRODUCTS;

  const extraProducts: ExtraProduct[] = [
    ...(hasTwoMutuallyExclusiveProducts ? mutuallyExclusiveComponents : []),
    ancillaryBundleComponent &&
      availableAncillaryBundlesFromQueryExist && { ...ancillaryBundleComponent },
    ...(isDBF
      ? getDBFProductsFrom(filteredSupportedProducts, structuredExtraProducts)
      : getProductsFrom(filteredSupportedProducts, structuredExtraProducts)
    ).map((product: GetProductsFromReturnType) => {
      const extraProduct = generateProductComponent(travelers, bounds, structuredExtraProducts);
      return extraProduct({
        ...product,
        shouldShowProductsPerTraveler,
        productsToDisplayPerTraveler,
      });
    }),
  ].map((product) => ({
    ...product,
    index: getProductIndex(product && 'id' in product ? product?.id : ''),
  }));

  const secondBagProduct: ExtraProduct | undefined = extraProducts.find(
    (secondBagExtraProduct) =>
      (secondBagExtraProduct && 'id' in secondBagExtraProduct && secondBagExtraProduct.id) ===
      secondBaggagePerTraveler.id,
  );

  const nonNullSecondBagProduct: ExtraProduct = secondBagProduct || ({} as ExtraProduct);

  const allProducts =
    includedExtraProducts && structuredExtraProducts
      ? [...includedExtraProducts, ...structuredExtraProducts]
      : [];

  const notAvailableProductList: SupportedProductsType = SUPPORTED_PRODUCTS.filter(
    isBaggage,
  ).filter(
    (supportedProduct) => !allProducts.find((prod) => supportedProduct.id === Number(prod?.id)),
  );

  const notAvailableProducts = getProductsFrom(SUPPORTED_PRODUCTS, notAvailableProductList).map(
    (product: GetProductsFromReturnType) => {
      const notAvailableProduct = generateProductComponent(
        travelers,
        bounds,
        notAvailableProductList,
      );
      return notAvailableProduct({
        ...product,
        isSelectable: false,
        shouldShowProductsPerTraveler,
        productsToDisplayPerTraveler,
      });
    },
  );

  const { filteredNotAvailableProducts, productToBeAddedAsIncluded } =
    getIncludedCabinBaggageComponent(
      notAvailableProducts,
      cabinBaggageTripSummary,
      productsToDisplayPerTraveler,
    );

  const includedProducts = [
    ...getProductsFrom(INCLUDED_PRODUCTS, nonNullIncludedExtraProducts).map((product) => {
      const includedProduct = generateProductComponent(
        travelers,
        bounds,
        (nonNullIncludedExtraProducts
          ? [...nonNullIncludedExtraProducts, nonNullSecondBagProduct]
          : [nonNullSecondBagProduct]) as any,
      );

      const {
        component,
        componentPerTraveler = undefined,
        id,
        code,
        __typename,
        persuasion,
        productIncludedTexts,
        selectedWithTrip,
        sellSpecification,
        productPreSelection,
        shouldShowPricePerBound,
      } = { ...product };

      return includedProduct({
        component,
        componentPerTraveler,
        id,
        code,
        __typename,
        persuasion,
        productIncludedTexts,
        selectedWithTrip,
        sellSpecification,
        productPreSelection,
        shouldShowPricePerBound,
        isSelectable: false,
        shouldShowProductsPerTraveler,
        productsToDisplayPerTraveler,
      });
    }),
    ...productToBeAddedAsIncluded,
  ];

  return {
    availableExtraProducts,
    cabinBaggageTripSummary,
    checkedBaggageSummary,
    climateCompensationPrice: getClimateCompensationPriceWhenSelectedWithTrip(extraProducts) as
      | number
      | null,
    includedProducts,
    notAvailableProducts: filteredNotAvailableProducts || notAvailableProducts,
    personalItemTripSummary,
    products: isDBF
      ? [extraProducts]
      : sortProducts(
          availableAncillaryBundlesFromQueryExist,
          isAncillaryBundlingEnabled,
          isProductsPrimarySecondaryEnabled,
          primaryProductCodes,
          productsSortOrder,
          extraProducts,
        ),
  };
};

const transformAncillaryBundlesData = (
  selectedTrip:
    | GetSelectedTripOnTravelerDetailsPageQuery['selectedTrip']
    | GetBookingStepPageDataQuery['selectedTrip'],
): {
  availableAncillaryBundlesFromQueryExist: boolean;
  bundles: AncillaryBundlesOnTdPageFragment[];
  bundlesTD: AncillaryBundlesOnTdPageFragment[];
} => {
  const availableAncillaryBundles = ((selectedTrip && 'availableAncillaryBundles' in selectedTrip
    ? selectedTrip.availableAncillaryBundles
    : selectedTrip?.dynamicBookingFlow?.stepProducts?.availableAncillaryBundles) ||
    []) as AncillaryBundlesOnTdPageFragment[];

  return {
    availableAncillaryBundlesFromQueryExist: Boolean(availableAncillaryBundles),
    bundles: availableAncillaryBundles.filter((bundle) => bundle.selectedOption !== null),
    bundlesTD: availableAncillaryBundles.filter((bundle) => bundle.selectedOption === null),
  };
};

export const transformData = (
  data: GetSelectedTripOnTravelerDetailsPageQuery | GetBookingStepPageDataQuery,
  siteContext: any,
  {
    formDateInputFormat,
    isAncillaryBundlingEnabled,
    isMutuallyExclusiveEnabled,
    isProductsPrimarySecondaryEnabled,
    isTravelArrangerEnabled,
    primaryProductCodes,
    productsPerTravelerExcludedProviderSystems,
    productsSortOrder,
    productsToDisplayPerTraveler,
    shouldShowProductsPerTraveler,
    shouldOverridePersuasion,
    shouldUsePersistedFormData,
    showConfirmEmail,
  }: TransformDataPropertiesPropsType,
  travelerInformationData?: GetTravelerInformationDataQuery,
) => {
  const { countries, selectedTrip, travelerDetails } = data;
  const isDBF = Boolean(
    selectedTrip && 'dynamicBookingFlow' in selectedTrip && selectedTrip.dynamicBookingFlow,
  );
  const { market, agencyAccount }: { market: CountryType; agencyAccount: AgencyAccount } =
    siteContext;
  const transformedAndSortedCountries = sortBy(
    countries.map((country) => transformCountry(country)),
    ['label'],
  );
  const { availableAncillaryBundlesFromQueryExist, bundles, bundlesTD } =
    transformAncillaryBundlesData(selectedTrip);
  const transformedBundleData = transformBundleData(bundlesTD[0]);

  const baggageInBundle = isBaggageSelectedWithBundle(bundles);

  const bounds = baggageInBundle
    ? populateBoundsWithBaggageInSelectedBundle(selectedTrip?.bounds ?? [], baggageInBundle)
    : selectedTrip?.bounds ?? [];
  const selfServiceRebookingTravelers =
    ('selfServiceRebooking' in data && data.selfServiceRebooking?.travelers) || [];

  const travelers: TransformedTravelersDataType = transformTravelersData(
    selectedTrip,
    travelerDetails,
    transformedAndSortedCountries,
    selfServiceRebookingTravelers,
  );

  const isSelfServiceRebooking =
    Array.isArray(selfServiceRebookingTravelers) && selfServiceRebookingTravelers.length > 0;

  const tripProviderSystems = selectedTrip?.systems;

  const shouldShowProductsPerTravelerNew =
    shouldShowProductsPerTraveler &&
    !hasExcludedSystemProvider(tripProviderSystems, productsPerTravelerExcludedProviderSystems);

  const {
    availableExtraProducts,
    cabinBaggageTripSummary,
    checkedBaggageSummary,
    climateCompensationPrice,
    includedProducts,
    notAvailableProducts,
    products,
    personalItemTripSummary,
  } = transformExtraProductsData({
    ancillaryBundleId:
      transformedBundleData && 'id' in transformedBundleData ? transformedBundleData.id : '',
    ancillaryBundleOptions:
      transformedBundleData && 'options' in transformedBundleData
        ? transformedBundleData.options
        : [],
    availableAncillaryBundlesFromQueryExist,
    bounds,
    discountPercentage:
      transformedBundleData && 'discountPercentage' in transformedBundleData
        ? transformedBundleData.discountPercentage
        : null,
    isAncillaryBundlingEnabled,
    isDBF,
    isProductsPrimarySecondaryEnabled,
    primaryProductCodes,
    productsSortOrder,
    productsToDisplayPerTraveler,
    selectedTrip,
    shouldOverridePersuasion: shouldOverridePersuasion ?? false,
    shouldShowProductsPerTraveler: shouldShowProductsPerTravelerNew,
    travelers,
    mutuallyExclusiveProducts:
      (selectedTrip &&
        'mutuallyExclusiveProducts' in selectedTrip &&
        selectedTrip.mutuallyExclusiveProducts) ||
      (selectedTrip &&
        'dynamicBookingFlow' in selectedTrip &&
        selectedTrip.dynamicBookingFlow &&
        selectedTrip.dynamicBookingFlow.stepProducts.mutuallyExclusiveProducts) ||
      [],
    isMutuallyExclusiveEnabled,
  });

  const includedOnTripProducts = addExtraProductsSelectedOnTrip(products);

  const getInitialTravelerValues = () => {
    if (travelerInformationData) {
      return transformTravelerInformationQueryToFormShape(
        travelerInformationData,
        market,
        formDateInputFormat,
      );
    }
    return getInitialValuesTravelerForm(
      travelers,
      market,
      countries,
      formDateInputFormat,
      shouldUsePersistedFormData,
      selfServiceRebookingTravelers,
      isSelfServiceRebooking,
    );
  };

  const initialValues = {
    ...(agencyAccount?.code
      ? getInitialValuesForAgencyForm(
          agencyAccount,
          market,
          transformedAndSortedCountries,
          data.selectedTrip?.customer,
        )
      : {}),
    ...getInitialValuesForContactInformationForm(
      market,
      transformedAndSortedCountries,
      shouldUsePersistedFormData,
      isTravelArrangerEnabled,
      showConfirmEmail,
      Boolean(travelerDetails?.shouldShowEmergencyContactInformation),
      agencyAccount?.code ? travelers[0]?.contactDetails : null,
    ),
    ...getInitialTravelerValues(),
    ...includedOnTripProducts,
    ...generateInitialValuesAncillaryBundles(bundles),
    ...generateInitialValuesExtraProducts(
      products,
      travelers,
      shouldShowProductsPerTravelerNew,
      productsToDisplayPerTraveler,
    ),
  };

  return {
    availableExtraProducts,
    bounds,
    bundles,
    bundlesTD,
    cabinBaggageTripSummary,
    checkedBaggageSummary,
    climateCompensationPrice,
    countries: transformedAndSortedCountries,
    includedCheckedBaggage: selectedTrip?.includedCheckedBaggage,
    includedProducts,
    initialValues,
    isMultiStop: selectedTrip?.type !== TripType.OneWay && selectedTrip?.type !== TripType.Return,
    isSelfServiceRebooking,
    notAvailableProducts,
    paymentMethodPrices: selectedTrip?.paymentMethodPrices?.map(transformPaymentMethod),
    personalItemTripSummary,
    products: filterProductCards(products),
    selectionKey: travelerDetails?.searchMetaData?.selectionKey ?? null,
    shareableUrl: selectedTrip?.shareableUrl,
    shouldShowEmergencyContactInformation: travelerDetails?.shouldShowEmergencyContactInformation,
    travelerPricesWithoutPaymentDiscounts: selectedTrip?.travelerPricesWithoutPaymentDiscounts,
    travelers,
    tripId: selectedTrip?.id,
    tripChange: getTripNotificationChanges(selectedTrip?.changes, bounds),
    tripCharacteristics: selectedTrip?.tripCharacteristics,
    tripOperatingCarriers: getTripOperatingCarriers(selectedTrip?.bounds ?? []),
    tripProviderSystems: selectedTrip?.systems,
    type: selectedTrip?.type,
    voucherAmount: selectedTrip?.voucherAmount,
  };
};
